import {
  LanguageNameSpaces,
  OpenMessage,
  TradeActionType,
  TradeData,
} from "@shared/types";
import {
  AccountObjectType,
  UseQuotaModal,
} from "@shared/features/settings/types";
import {
  HandleTradeActionClick,
  OpenShareResultsModalType,
} from "@shared/features/trades/common/types";
import { useCallback, useState } from "react";
import {
  getBotManagedTradeEditWarning,
} from "@shared/features/trades/common/constants";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import { useClientsData, useExchangesMetadata } from "@shared/utils/hooks/misc";
import { getReadOnlyAccountWarning } from "@shared/utils/misc";

interface useTradeActionsProviderProps {
  openMessage: OpenMessage;
  useQuotaModal: UseQuotaModal;
  openShareResultsModal: OpenShareResultsModalType;
}

const useTradeActionsProvider = ({
  openShareResultsModal,
  useQuotaModal,
  openMessage,
}: useTradeActionsProviderProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [isMoveEntryModalVisible, setIsMoveEntryModalVisible] = useState(false);
  const [isSellPartiallyModalVisible, setIsSellPartiallyModalOpen] =
    useState(false);
  const [modalTradeData, setModalTradeData] = useState<TradeData | undefined>();
  const [isCancelTradeModalVisible, setIsCancelTradeModalVisible] =
    useState(false);
  const { quotaModal, handleTriggerClick } = useQuotaModal({});
  const { getInvalidAccountData } = useInvalidAccount();
  const { getAccountIsReadOnly } = useClientsData();
  const { getIsFree } = useExchangesMetadata();

  const showNoEditPermissionMessage = () => {
    openMessage?.(
      getReadOnlyAccountWarning(AccountObjectType.TRADE),
      "warning"
    );
  };

  const getIsTradeReadOnlyAccount = useCallback(
    (tradeData?: TradeData) => {
      return getAccountIsReadOnly(tradeData?.client_id);
    },
    [getInvalidAccountData]
  );

  const getEditTradeErrorMessage = useCallback(
    (tradeData: TradeData) => {
      const botManagedTradeEditWarning = getBotManagedTradeEditWarning(
        tradeData.source_type
      );
      if (botManagedTradeEditWarning) return t(botManagedTradeEditWarning);
      const invalidAccountData = getInvalidAccountData(tradeData.client_id);
      if (invalidAccountData.color) {
        return invalidAccountData.isInvalid
          ? t("warnings.invalid_account_edit")
          : t("warnings.inactive_account_edit");
      }
    },
    [getInvalidAccountData]
  );

  const getMoveEntryErrorMessage = useCallback(
    (tradeData: TradeData) => {
      const editTradeError = getEditTradeErrorMessage(tradeData);
      if (editTradeError) return editTradeError;
      if (!tradeData?.next_entry) {
        return t("warnings.no_pending_entries");
      }
    },
    [getEditTradeErrorMessage, i18n.language]
  );

  const getSellPartiallyErrorMessage = useCallback(
    (tradeData: TradeData) => {
      const editTradeError = getEditTradeErrorMessage(tradeData);
      if (editTradeError) return editTradeError;
      if (!tradeData?.invested_amount) {
        return t("warnings.partial_sell_requirement");
      }
    },
    [getEditTradeErrorMessage, i18n.language]
  );

  const openMoveEntryModal = (tradeData: TradeData) => {
    const error = getMoveEntryErrorMessage(tradeData);
    if (error) {
      openMessage(
        t(error as TranslationKeys<LanguageNameSpaces.TRADES>),
        "warning"
      );
    } else {
      setModalTradeData(tradeData);
      setIsMoveEntryModalVisible(true);
    }
  };

  const openSellPartiallyModal = (tradeData: TradeData) => {
    const error = getSellPartiallyErrorMessage(tradeData);
    if (error) {
      openMessage(
        t(error as TranslationKeys<LanguageNameSpaces.TRADES>),
        "warning"
      );
    } else {
      setModalTradeData(tradeData);
      setIsSellPartiallyModalOpen(true);
    }
  };

  const handleEditTradeAction = (tradeData: TradeData, action?: () => void) => {
    const error = getEditTradeErrorMessage(tradeData);
    if (error) {
      openMessage(
        t(error as TranslationKeys<LanguageNameSpaces.TRADES>),
        "warning"
      );
    } else {
      action?.();
    }
  };

  const openCancelTradeModal = (tradeData: TradeData) => {
    setModalTradeData(tradeData);
    setIsCancelTradeModalVisible(true);
  };

  const closeSellPartiallyModal = useCallback(() => {
    setIsSellPartiallyModalOpen(false);
    setModalTradeData(undefined);
  }, []);

  const closeMoveEntryModal = useCallback(() => {
    setIsMoveEntryModalVisible(false);
    setModalTradeData(undefined);
  }, []);

  const closeCancelTradeModal = useCallback(() => {
    setIsCancelTradeModalVisible(false);
    setModalTradeData(undefined);
  }, []);

  const handleTradeActionClick: HandleTradeActionClick = useCallback(
    async (actionType, tradeData, action, openShareModalDelay) => {
      if (getAccountIsReadOnly(tradeData?.client_id)) {
        showNoEditPermissionMessage();
        return;
      }
      if (actionType === TradeActionType.SHARE_RESULTS) {
        if (tradeData) {
          const accountData = getInvalidAccountData(tradeData.client_id);
          openShareResultsModal(
            tradeData.id,
            tradeData.symbol,
            tradeData.source_type,
            accountData?.isInvalidOrInactive,
            undefined,
            openShareModalDelay
          );
        }
        return;
      }
      if (actionType !== TradeActionType.CLOSE_TRADE) {
        if (!getIsFree(tradeData?.exchange)) {
          const isExpired = await handleTriggerClick();
          if (isExpired) return;
        }
      }
      if (actionType === TradeActionType.CLOSE_TRADE) {
        if (action) action();
        else if (tradeData) openCancelTradeModal(tradeData);
      } else if (
        actionType === TradeActionType.EDIT_ORDERS ||
        actionType === TradeActionType.EDIT_CONFIGURATION
      ) {
        tradeData && handleEditTradeAction(tradeData, action);
      } else if (actionType === TradeActionType.MOVE_NEAREST_ENTRY) {
        tradeData && openMoveEntryModal(tradeData);
      } else if (actionType === TradeActionType.SELL_PARTIALLY) {
        tradeData && openSellPartiallyModal(tradeData);
      }
    },
    [getInvalidAccountData, handleTriggerClick, getAccountIsReadOnly, getIsFree]
  );

  return {
    isMoveEntryModalVisible,
    isSellPartiallyModalVisible,
    isCancelTradeModalVisible,
    modalTradeData,
    closeSellPartiallyModal,
    closeMoveEntryModal,
    getMoveEntryErrorMessage,
    getSellPartiallyErrorMessage,
    getEditTradeErrorMessage,
    closeCancelTradeModal,
    quotaModal,
    handleTradeActionClick,
    getIsTradeReadOnlyAccount,
  };
};

export default useTradeActionsProvider;
